import { useTranslation } from 'react-i18next';
import { withStyles, BodyText, Box, Card, BrandingProductType, FieldData, UseFormReturn } from 'vopay';
import { Title, Button } from '#shared/components/UI';
import useBrandingDiscardDialog from '../useBrandingDiscardDialog';
import { BrandingValues, showsPortalFields } from './useBrandingSettingsFormConfig';
import styles from './BrandingSettingsForm.module.scss';

export type { BrandingValues } from './useBrandingSettingsFormConfig';

const cn = withStyles(styles);

type BrandingSettingsFormProps = {
  productType: BrandingProductType;
  productLabel: string;
  readOnly: boolean;
  isEditing: boolean;
  onEdit: () => void;
  onCancel: () => void;
  ctx: UseFormReturn<BrandingValues>['ctx'];
  renderFormElements: UseFormReturn<BrandingValues>['renderFormElements'];
  logoFields: FieldData<BrandingValues>[];
  colorFields: FieldData<BrandingValues>[];
  applyToAllField: FieldData<BrandingValues>[];
};

export default function BrandingSettingsForm({
  productType,
  productLabel,
  readOnly,
  isEditing,
  onEdit,
  onCancel,
  ctx,
  renderFormElements,
  logoFields,
  colorFields,
  applyToAllField,
}: BrandingSettingsFormProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.Branding.Form' });
  const confirmDiscard = useBrandingDiscardDialog();

  async function handleCancel() {
    if (ctx.hasPendingData && !(await confirmDiscard())) return;

    ctx.resetForm();
    onCancel();
  }

  return (
    <form onSubmit={ctx.handleSubmit}>
      <Card className={cn('card')}>
        <Box flx="col" gapSize="32px">
          <div className={cn('cardHeader')}>
            <Box flx="col" gapSize="6px">
              <Title level="4">{t('cardTitle', { defaultValue: '{{product}} branding', product: productLabel })}</Title>

              <BodyText size="sm" className={cn('cardSubtitle')}>
                {t('cardSubtitle', {
                  defaultValue: 'Changes apply to {{product}} only, unless you tick Apply to all products.',
                  product: productLabel,
                })}
              </BodyText>
            </Box>

            <Box gapSize="12px">
              {!isEditing && !readOnly && (
                <Button colorScheme="primary" type="button" onClick={onEdit}>
                  {t('editButton', { defaultValue: 'Edit' })}
                </Button>
              )}

              {isEditing && (
                <Button colorScheme="secondary" type="button" disabled={ctx.isSubmitting} onClick={handleCancel}>
                  {t('cancelButton', { defaultValue: 'Cancel' })}
                </Button>
              )}

              {isEditing && (
                <Button
                  colorScheme="primary"
                  type="submit"
                  disabled={!ctx.hasPendingData || !ctx.isValid || ctx.isSubmitting}
                >
                  {t('saveButton', { defaultValue: 'Save' })}
                </Button>
              )}
            </Box>
          </div>

          <div className={cn('divider')} />

          <div className={cn('section')}>
            <Box flx="col" gapSize="4px">
              <Title level="4">{t('brandLogoHeading', { defaultValue: 'Brand logo' })}</Title>

              {isEditing && (
                <BodyText size="sm" className={cn('sectionHelpText')}>
                  {t('brandLogoHelpText', {
                    defaultValue:
                      "SVG only, up to 20 MB. A wide, horizontal logo works best. It's scaled to fit each product.",
                  })}
                </BodyText>
              )}
            </Box>

            <div className={cn('fieldsRow')}>{renderFormElements({ config: logoFields })}</div>
          </div>

          <div className={cn('section')}>
            <Title level="4">{t('brandColoursHeading', { defaultValue: 'Brand colours' })}</Title>

            {isEditing && (
              <BodyText size="sm" className={cn('sectionHelpText')}>
                {t('brandColoursHelpText', {
                  defaultValue: 'Enter a HEX code or pick a colour. The preview updates as you go.',
                })}
              </BodyText>
            )}

            <div className={cn('fieldsRow')}>{renderFormElements({ config: colorFields })}</div>
          </div>

          {isEditing && (
            <div className={cn('footer')}>
              {renderFormElements({ config: applyToAllField, override: (fieldData) => fieldData.template })}

              {!showsPortalFields(productType) && (
                <BodyText size="sm" className={cn('footerHelpText')}>
                  {t('applyToAllHelpText', {
                    defaultValue:
                      'Also applies to VoPay Portal, which needs a small logo and a secondary colour. Those fields will appear when you tick this.',
                  })}
                </BodyText>
              )}
            </div>
          )}
        </Box>
      </Card>
    </form>
  );
}
